import React, { Component } from "react";
import { connect } from "react-redux";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import moment from "moment";
import DatePicker from "react-datepicker";
import Select from "react-select";
import CustomDatePickerInput from "../_common/CustomDatePickerInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import issueActions from "../../_actions/issueActions";

class AddIssueModal extends Component {
  state = {
    formData: {
      createdOn: new Date(),
      issueTypeId: 0,
      selectedIssueType: null,
      issuePriorityId: 0,
      selectedIssuePriority: null,
      title: "",
      bodyText: "",
      addIssueEditorState: "",
      projectEa: "",
      assignedProjectRole: "",
      selectedProjectRole: null,
      affectedModule: "",
      selectedModule: null,
      issueStatusId: 0, // This will always be 0
      reviewedByUserId: null,
      respondedOn: null,
      isResolved: false,
      isClosed: false,
      posts: [],
    },
    formErrors: {
      createdOn: null,
      issueType: null,
      issuePriority: null,
      title: null,
      bodyText: null,
      affectedModule: null,
    },
    issueDate: new Date(),
  };

  typeOptions = this.props.typeEnum.map((label, value) => ({ value, label }));
  priorityOptions = [
    { value: 0, label: "Low" },
    { value: 1, label: "Medium" },
    { value: 2, label: "High" },
    { value: 3, label: "Critical" },
  ];

  componentDidMount() {
    this.setState({
      formData: {
        ...this.state.formData,
        createdOn: new Date(),
        issueTypeId: 0,
        selectedIssueType: this.typeOptions[0],
        issuePriorityId: 0,
        selectedIssuePriority: this.priorityOptions[0],
      },
    });
  }

  // Only Bug type requires priority
  // Questions and Feedback are defaulted to Low
  requiresPriority = () => {
    const { formData } = this.state;
    // issueTypeId: 0 = Bug, 1 = Suggestion, 2 = Question
    return formData.issueTypeId === 0;
  };

  handleChange_editor = (addIssueEditorState) => {
    this.setState({
      formData: { ...this.state.formData, addIssueEditorState },
    });
  };

  handleChange_input = (e) =>
    this.setState({
      formData: { ...this.state.formData, [e.target.name]: e.target.value },
    });

  handleChange_date = (issueDate) => this.setState({ issueDate });

  handleChange_select = (fieldName, optionFieldName) => (selectedOption) => {
    const formData = { ...this.state.formData };
    formData[fieldName] = selectedOption.value;
    formData[optionFieldName] = selectedOption;

    // If changing issue type to Question or Suggestion, clear priority
    if (fieldName === "issueTypeId" && selectedOption.value !== 0) {
      formData.issuePriorityId = 0;
      formData.selectedIssuePriority = this.priorityOptions[0];
    }

    this.setState({ formData }, () => this.validateForm(fieldName));
  };

  handleBlur_addIssueForm = (e) => this.validateForm(e.target.name);
  handleBlur_select = (fieldName) => () => this.validateForm(fieldName);

  validateForm = (fieldName) => {
    const { formData } = this.state;
    const errors = { ...this.state.formErrors };
    switch (fieldName) {
      case "createdOn":
        if (formData.createdOn === null) errors.createdOn = "Issue Date is required.";
        else errors.createdOn = null;
        break;
      case "issueTypeId":
        if (formData.selectedIssueType === null)
          errors.issueType = "Issue Type is required.";
        else errors.issueType = null;
        break;
      case "issuePriorityId":
        // Only validate priority if issue type is Bug
        if (this.requiresPriority() && formData.selectedIssuePriority === null)
          errors.issuePriority = "Issue Priority is required for Bugs.";
        else errors.issuePriority = null;
        break;
      case "bodyText":
        if (formData.bodyText === "") errors.bodyText = "Description is required.";
        else errors.bodyText = null;
        break;
      default:
        break;
    }
    this.setState({ formErrors: errors });
  };

  formIsIncomplete = () => {
    const { createdOn, issueType, issuePriority, bodyText } = this.state.formErrors;
    const { formData } = this.state;

    // Base requirements
    if (
      createdOn ||
      issueType ||
      bodyText ||
      formData.createdOn === null ||
      formData.selectedIssueType === null ||
      formData.bodyText === null ||
      formData.bodyText === ""
    )
      return true;

    // Priority is only required for Bug type (issueTypeId === 0)
    if (this.requiresPriority() && (issuePriority || formData.selectedIssuePriority === null))
      return true;

    return false;
  };

  resetFormData = () => {
    const { formData } = this.state;
    formData.createdOn = new Date();
    formData.issueTypeId = 0;
    formData.selectedIssueType = this.typeOptions[0];
    formData.issuePriorityId = 0;
    formData.selectedIssuePriority = this.priorityOptions[0];
    this.setState({ formData });
  };

  handleSubmit_addIssueForm = (e) => {
    const { dispatch, version, toggleAddIssueModal, userId } = this.props;
    const formData = { ...this.state.formData };
    e.preventDefault();
    if (!this.formIsIncomplete()) {
      // Save current userId as creatorUserId
      formData.createdByUserId = userId;
      formData.versionReported = version;
      formData.createdOn = moment(formData.createdOn).format();

      // If not a Bug, set priority to Low (0) by default
      if (!this.requiresPriority()) {
        formData.issuePriorityId = 0;
      }

      dispatch(issueActions.addIssue(formData))
        .then(() => {
          this.resetFormData();
          toggleAddIssueModal();
        })
        .catch((err) => {
          console.error("Error adding issue:", err);
        });
    }
  };

  render() {
    const { showAddIssueModal, toggleAddIssueModal } = this.props;
    const { formData, issueDate, formErrors } = this.state;
    const showPriority = this.requiresPriority();

    const descStyle = {
      lineHeight: "1.0",
    };

    return (
      <React.Fragment>
        <Modal
          isOpen={showAddIssueModal}
          toggle={toggleAddIssueModal}
          className={this.props.className}
          size={"lg"}
          fade={false}
        >
          <ModalHeader toggle={toggleAddIssueModal} className="bg-lightgray">
            <FontAwesomeIcon icon="bug" className="fa-fw mr-2" /> Report an Issue
          </ModalHeader>
          <ModalBody>
            <form
              id="addIssueForm"
              onSubmit={this.handleSubmit_addIssueForm}
              autoComplete={"off"}
            >
              <div className="card m-2">
                <div className="card-body">
                  <div className="form-row form-group mb-2">
                    <label htmlFor="createdOn" className="col-sm-3 col-form-label">
                      <b>Issue Date:</b>*
                    </label>
                    <div className="col-sm-4">
                      <DatePicker
                        customInput={<CustomDatePickerInput />}
                        id="createdOn"
                        name="createdOn"
                        selected={issueDate}
                        dateFormat="MM/dd/yyyy (E)"
                        onChange={this.handleChange_date}
                        onBlur={this.handleBlur_addIssueForm}
                      />
                    </div>
                  </div>
                  <div className="form-row form-group mb-2">
                    <label htmlFor="issueTypeId" className="col-sm-3 col-form-label">
                      <b>Issue Type:</b>*
                    </label>
                    <div className="col-sm-4">
                      <Select
                        name="issueTypeId"
                        id="issueTypeId"
                        value={formData.selectedIssueType}
                        options={this.typeOptions}
                        onChange={this.handleChange_select(
                          "issueTypeId",
                          "selectedIssueType"
                        )}
                        onBlur={this.handleBlur_select("issueTypeId")}
                      />
                      {formErrors.issueType && (
                        <span className="error-small">
                          <small>{formErrors.issueType}</small>
                        </span>
                      )}
                    </div>
                  </div>
                  {showPriority && (
                    <div className="form-row form-group mb-2">
                      <label htmlFor="issuePriorityId" className="col-sm-3 col-form-label">
                        <b>Issue Priority:</b>*
                      </label>
                      <div className="col-sm-4">
                        <Select
                          name="issuePriorityId"
                          id="issuePriorityId"
                          value={formData.selectedIssuePriority}
                          options={this.priorityOptions}
                          onChange={this.handleChange_select(
                            "issuePriorityId",
                            "selectedIssuePriority"
                          )}
                          onBlur={this.handleBlur_select("issuePriorityId")}
                        />
                        {formErrors.issuePriority && (
                          <span className="error-small">
                            <small>{formErrors.issuePriority}</small>
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="form-row form-group mb-2">
                    <label htmlFor="title" className="col-sm-3 col-form-label">
                      <b>Title:</b>*
                    </label>
                    <div className="col-sm-9">
                      <input
                        name="title"
                        id="title"
                        className="form-control"
                        maxLength="300"
                        value={formData.title}
                        onChange={this.handleChange_input}
                        onBlur={this.handleBlur_addIssueForm}
                      />
                      {formErrors.title && (
                        <span className="error-small">
                          <small>{formErrors.title}</small>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="form-row form-group mb-2">
                    <div className="col-sm-3">
                      <label htmlFor="bodyText" className="col-form-lab">
                        <b>Description:</b>*
                      </label>
                      <p style={descStyle}>
                        <span>
                          <small>
                            {showPriority ? (
                              <>
                                Include details regarding:
                                <br />
                                1) Your attempted action(s), and
                                <br />
                                2) The actual results (include error messages).
                              </>
                            ) : (
                              <>
                                Please provide details about your{" "}
                                {formData.issueTypeId === 1 ? "suggestion" : "question"}.
                              </>
                            )}
                          </small>
                        </span>
                      </p>
                    </div>
                    <div className="col-sm-9">
                      <textarea
                        id="bodyText"
                        name="bodyText"
                        className="form-control"
                        rows="6"
                        value={formData.bodyText}
                        onChange={this.handleChange_input}
                        onBlur={this.handleBlur_addIssueForm}
                      />
                      {formErrors.bodyText && (
                        <span className="error-small">
                          <small>{formErrors.bodyText}</small>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </ModalBody>
          <ModalFooter className="bg-lightgray">
            <div className="form-row form-group mb-0">
              <input
                className="btn btn-primary"
                name="addNewIssueButton"
                type="submit"
                form="addIssue"
                onClick={this.handleSubmit_addIssueForm}
                value="Send"
                disabled={this.formIsIncomplete()}
              />{" "}
              <button
                className="btn btn-secondary ml-2"
                color="secondary"
                onClick={toggleAddIssueModal}
              >
                Cancel
              </button>
            </div>
          </ModalFooter>
        </Modal>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  version: state.appSettings.version || "(N/A)",
});

export default connect(mapStateToProps)(AddIssueModal);
